import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { isEmpty } from "ramda";
import { Row, Col, Spinner } from "reactstrap";

import { isMobile } from "react-device-detect";
import {
  CftLayout,
  CftHeader,
  CftSection,
  CftFormControl,
  CftTank,
  CftActionsButtons,
  CftReport,
  CftCard,
  CftTable,
  CftGraph,
  CftGroup,
  CftReportNavigation,
  GasEmissionNav,
  CostStructureNav,
  CftCostStructure,
  CftReportDetailsSection,
  CftInfoBox,
  SpecificInformationNav,
  CftExportReport,
  CftMobileReportWrapper,
} from "../../components";
import { useCft } from "../../utils/CftProvider";
import routes from "../../utils/cftRoutes";
import { dairyProductionAPI } from "../../utils/cftApi";
import { nFormatter } from "../../utils/cftUtils";
import useDairyDetailsReport from "../../utils/cftDairyDetailsReport";

import "../styles/intro.scss";

function ReportDairyProductionPage() {
  const [data, setData] = useState([]);
  const [report, setReport] = useState(null);
  const [isPending, setPending] = useState(false);
  const { t, i18n } = useTranslation(["cft"]);
  const history = useHistory();
  const { meta, dairyProductionReport } = useCft();
  const { details } = useDairyDetailsReport(dairyProductionReport);

  const isValid =
    !isEmpty(dairyProductionReport.milk_production) &&
    !isEmpty(dairyProductionReport.herd_sections);

  const getFactor = scale => {
    const x = scale.max === 0 || scale.min === 0 ? 280 : 150;
    const value =
      Math.abs(scale.min) > Math.abs(scale.max)
        ? Math.abs(scale.min)
        : Math.abs(scale.max);

    return x / value;
  };

  useEffect(() => {
    if (isValid) {
      setPending(true);
      dairyProductionAPI
        .report({
          ...dairyProductionReport,
          feed_aproche: dairyProductionReport.feed_aproche || undefined,
          fertilisation_approach:
            dairyProductionReport.fertilisation_approach || undefined,
          grazing_area: isEmpty(dairyProductionReport.grazing_area)
            ? undefined
            : dairyProductionReport.grazing_area,
        })
        .then(({ data }) => {
          setReport(data);
          const scale = data.total_emissions.reduce(
            (prev, curr) => {
              const min =
                prev.min < curr.total_co2e ? prev.min : curr.total_co2e;
              const max =
                prev.max > curr.total_co2e ? prev.max : curr.total_co2e;

              return {
                min,
                max,
              };
            },
            {
              min: 0,
              max: 0,
            }
          );

          const factor = getFactor(scale);

          const parsedData = data.total_emissions.map(
            ({ name, total_co2e }) => ({
              factor,
              value: total_co2e * factor,
              rawValue: total_co2e,
              label: name,
              legend: nFormatter(total_co2e, i18n.language),
              scale,
            })
          );

          setData(parsedData);
        })
        .finally(() => {
          setPending(false);
        });
    }
  }, []);

  return (
    <CftLayout
      header={
        <>
          <CftHeader />
          {isMobile && (
            <div
              style={{
                position: "fixed",
                top: "1%",
                zIndex: "1000",
                right: "1%",
              }}
            >
              <CftExportReport type="dairyProduction" isSmall noMargin />
            </div>
          )}
          <CftReportNavigation type="dairyProduction" />
        </>
      }
    >
      <CftSection
        isReport
        header={
          <CftTank
            className="report-hero"
            flexDirection="column"
            alignItems="flex-start"
          >
            <CftActionsButtons.GoToCalc
              onClick={() => history.push(routes.DAIRY_PRODUCTION.MAIN)}
            />
            <h3>
              {t("cft:report.title", "Raport emisji")} CO<sub>2</sub>
            </h3>
            <p className="full-width">{meta.title.dairyProduction}</p>
          </CftTank>
        }
      >
        {!isValid && (
          <CftFormControl variant="small">
            <h3 className="cft-headline">
              {t("cft:report.missingData", "Brak danych")}

              <small>
                {t(
                  "cft:report.requirements",
                  "Do wygenerowania raportu wymagane są następujące dane: Szczegóły hodowli, Charakterystyka stada oraz Wypas bydła"
                )}
              </small>
            </h3>
          </CftFormControl>
        )}

        {isValid && isPending && (
          <CftTank justifyContent="center" mb="2rem">
            <Spinner color="success" />
          </CftTank>
        )}
        {report && (
          <div id="report">
            <CftGroup header={<GasEmissionNav />} id="gasEmission">
              <CftCard>
                <Row>
                  <Col xs={12} md={4} lg={3}>
                    <CftReport.Box paddingRight={0}>
                      <CftReport.Summary
                        data={[
                          {
                            value: nFormatter(
                              report.summary.emissions_total.value,
                              i18n.language
                            ),
                            unit: report.summary.emissions_total.unit,
                            title: t(
                              "cft:report.summary.totalEmmisions",
                              "Całkowita emisja"
                            ),
                          },
                          {
                            value: nFormatter(
                              report.summary.emissions_per_fpcm.value,
                              i18n.language
                            ),
                            unit: report.summary.emissions_total.unit,
                            title: t(
                              "cft:report.summary.emissionsPerFpcm",
                              "Emisje na fpcm"
                            ),
                          },
                        ]}
                      />
                    </CftReport.Box>
                  </Col>
                  <Col xs={12} md={8} lg={9}>
                    <CftReport.Box>
                      <CftMobileReportWrapper>
                        <CftGraph data={data} />
                      </CftMobileReportWrapper>
                    </CftReport.Box>
                  </Col>
                </Row>
                <CftReport.Box paddingTop={0}>
                  <CftReport.Divider />
                  <CftReport.Title>
                    {t("cft:report.title", "Szczegółowe dane")}
                  </CftReport.Title>
                </CftReport.Box>
                <CftMobileReportWrapper>
                  <CftTable data={report.total_emissions} />
                </CftMobileReportWrapper>
                <CftReport.Box>
                  <CftInfoBox>{t("cft:report.info1")}</CftInfoBox>
                </CftReport.Box>
              </CftCard>
            </CftGroup>
            <CftGroup
              header={<SpecificInformationNav />}
              id="specificInformation"
            >
              <CftCard>
                <CftReportDetailsSection data={details} />
              </CftCard>
            </CftGroup>
            <CftGroup header={<CostStructureNav />} id="costStructure">
              <CftCostStructure name={meta.title.dairyProduction} />
            </CftGroup>
            <p className="cft-disclaimer">{t("cft:report.disclaimer")}</p>
          </div>
        )}
      </CftSection>
    </CftLayout>
  );
}

export default ReportDairyProductionPage;
